import IEventSubscription from 'generic/IEventSubscription';
import Cookies from 'js-cookie';

const ALLOW_COOKIE_STORE_FALLBACK = true;
const CACHE_NAME = 'rock-maker-cache';
const addListeners = new Map<CacheKey, Set<(value: any) => void>>();
const removeListeners = new Map<CacheKey, Set<(value: any) => void>>();

type CacheKey = 'token' | 'X-MachineName';
interface ICache {
  put(key: string, value: any): Promise<void>;
  get(key: string): Promise<any | null>;
  delete(key: string): Promise<boolean>;
}
class CookieBasedCache implements ICache {
  async put(key: string, value: any) {
    Cookies.set(key, JSON.stringify(value));
  }
  async get(key: string) {
    const val = Cookies.get(key);
    if (!val || val.trim() === '') {
      return null;
    }
    return JSON.parse(val);
  }
  async delete(key: string) {
    Cookies.remove(key);
    return true;
  }
}
class CacheImpl implements ICache {
  constructor(private readonly cache: Cache) {
    //
  }
  put(key: string, value: any) {
    const response = new Response(JSON.stringify(value), {
      headers: { 'Content-Type': 'application/json' },
    });
    return this.cache.put(new Request(key), response);
  }
  async get(key: string) {
    const resp = await this.cache.match(new Request(key));
    if (!resp) {
      return null;
    }
    return await resp.json();
  }
  delete(key: string) {
    return this.cache.delete(new Request(key));
  }
}

async function openCache(): Promise<ICache> {
  try {
    const cache = await caches.open(CACHE_NAME);
    return new CacheImpl(cache);
  } catch (error) {
    if (ALLOW_COOKIE_STORE_FALLBACK) {
      console.warn('failed to open cache, using cookie instead ', { error });
      return new CookieBasedCache();
    }
    throw error;
  }
}

const triggerListener = (
  key: CacheKey,
  value: any,
  listeners: Map<CacheKey, Set<(value: any) => void>>,
): void => {
  const callbacks = listeners.get(key);
  if (!callbacks?.size) {
    return;
  }

  // trigger callbacks in another thread
  Promise.resolve().then(() => {
    for (const cb of callbacks) {
      try {
        cb(value);
      } catch (err) {
        console.error(`Listener for "${key}" failed:`, err);
        // continue to next listener
      }
    }
  });
};

export const CacheHelper = {
  async put(key: CacheKey, value) {
    const cache = await openCache();
    await cache.put(key, value);
    triggerListener(key, value, addListeners);
  },

  async get(key: CacheKey) {
    const cache = await openCache();
    return await cache.get(key);
  },

  async delete(key: CacheKey) {
    const cache = await openCache();
    await cache.delete(key);
    triggerListener(key, null, removeListeners);
  },

  oncacheadded(key: CacheKey, callback: (value: any) => void): IEventSubscription {
    if (!addListeners.has(key)) {
      addListeners.set(key, new Set());
    }
    addListeners.get(key)!.add(callback);

    return {
      dispose: () => {
        addListeners.get(key)?.delete(callback);
        if (addListeners.get(key)?.size === 0) {
          addListeners.delete(key);
        }
      },
    };
  },

  oncacheremoved(key: CacheKey, callback: (value: any) => void): IEventSubscription {
    if (!removeListeners.has(key)) {
      removeListeners.set(key, new Set());
    }
    removeListeners.get(key)!.add(callback);

    return {
      dispose: () => {
        removeListeners.get(key)?.delete(callback);
        if (removeListeners.get(key)?.size === 0) {
          removeListeners.delete(key);
        }
      },
    };
  },
};
